import * as React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Helmet } from "react-helmet";
import { BG, CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { FONT_FAMILY, FA_DANGER_MENU_COLOR, WARN_YELLOW } from "./designTokens";

// The page container, repeated byte-for-byte on twelve pages — including inside
// FAStates, which had to keep its own copy so the loading skeleton lined up with the
// content that replaced it. That coupling is what made it worth extracting: the two could
// silently disagree, and a page would visibly shift as it resolved.
const CONTAINER_SX = {
  px: { xs: "16px", sm: "32px" },
  pt: "83px",
  maxWidth: "860px",
  mx: "auto",
};

// Pages with a fixed action bar need to clear it as well as the bottom nav.
const CONTENT_PB = "120px";
const FOOTER_PB = "170px";

export { CONTAINER_SX, CONTENT_PB, FOOTER_PB };

/**
 * Standard Field Admin page frame: the dark body style and the centred container.
 *
 * Not used by HSPageShell or StockFormShell — those are their own shells with a step
 * strip and a sticky footer respectively, and they set their own gutters.
 *
 * `footer` reserves room for a fixed action bar.
 */
export function FAPageShell({ children, footer, sx }) {
  return (
    <>
      <Helmet>
        <style>{`body { background: ${BG}; font-family: ${FONT_FAMILY}; }`}</style>
      </Helmet>
      <Box sx={{ ...CONTAINER_SX, pb: footer ? FOOTER_PB : CONTENT_PB, ...sx }}>
        {children}
      </Box>
    </>
  );
}

/**
 * The design's .page-head — title, optional sub-line, optional right-hand action.
 *
 * The title is responsive: the design drops it to 1.4rem under 720px, which only Sites
 * implemented (and via MUI's md breakpoint, 900px, because none of MUI's defaults land on
 * 720). A raw media query matches the design exactly.
 *
 * `sub` takes a node, since several pages colour part of it amber.
 */
export function FAPageHeader({ title, sub, action, sx }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: "16px",
        mb: "18px",
        flexWrap: "wrap",
        ...sx,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "1.75rem",
            fontWeight: 700,
            color: "#fff",
            letterSpacing: "-0.025em",
            lineHeight: 1.15,
            "@media (max-width:720px)": { fontSize: "1.4rem" },
          }}
        >
          {title}
        </Typography>
        {sub ? (
          <Typography sx={{ fontSize: "0.86rem", color: "rgba(255,255,255,0.55)", mt: "3px" }}>
            {sub}
          </Typography>
        ) : null}
      </Box>
      {action || null}
    </Box>
  );
}

// Layout primitives shared across the Field Admin pages. Each of these existed as several
// per-page copies that had drifted apart in ways the design does not call for.

// The design's .section-label. Its typography is byte-identical in all 14 declarations
// across the design pages — only the vertical padding varies per page, which is why that
// stays overridable. Four separate components had grown around this one string
// (HSSectionLabel, plus local SectionLabels in More, Profile and StockIssue).
const LABEL_SX = {
  fontSize: "0.66rem",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  fontWeight: 700,
  color: "rgba(255,255,255,0.4)",
};

// .section-label .count — 0.7rem at 0.5 alpha, weight 500, and explicitly NOT uppercased
// or letter-spaced, in all seven design declarations.
const COUNT_SX = {
  fontSize: "0.7rem",
  color: "rgba(255,255,255,0.5)",
  fontWeight: 500,
  textTransform: "none",
  letterSpacing: 0,
};

/**
 * A section heading, optionally with a right-hand count or an amber step chip.
 *
 * `right`  — plain muted text (the design's .count), e.g. "4 vehicles".
 * `chip`   — the amber pill the stock forms use for "Step 1 of 2".
 * `sx`     — padding override; the design's dominant value is the default.
 */
export function FASectionLabel({ children, right, chip, sx }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "10px",
        pt: "4px",
        pb: "12px",
        ...sx,
      }}
    >
      <Typography sx={LABEL_SX}>{children}</Typography>
      {chip ? (
        <Box
          component="span"
          sx={{
            fontSize: "0.64rem", fontWeight: 700, px: "8px", py: "3px", borderRadius: "4px",
            backgroundColor: "rgba(253,185,64,0.16)", color: AMBER, whiteSpace: "nowrap",
          }}
        >
          {chip}
        </Box>
      ) : null}
      {right && !chip ? <Typography sx={COUNT_SX}>{right}</Typography> : null}
    </Box>
  );
}

/**
 * The filter chip row on Fleet, Devices, Sites and Alerts.
 *
 * All four were the same markup with different omissions: three rendered a click handler
 * on a <div>, so only Alerts' was keyboard-reachable; only Sites carried the hover state.
 * The design specifies both — `<button class="chip-filter" type="button">` with
 * `.chip:hover { border-color: rgba(253,185,64,0.4); color: #FDB940 }` — so the shared
 * version takes the semantics from one and the hover from the other.
 *
 * `counts` is optional: a chip with no matching entry renders as a plain label.
 */
export function FAFilterChips({ filters, counts, active, onChange }) {
  return (
    <Box sx={{ display: "flex", gap: "8px", mb: "16px", flexWrap: "wrap" }}>
      {filters.map((filter) => {
        const isActive = active === filter;
        const count = counts ? counts[filter] : undefined;
        return (
          <Box
            key={filter}
            component="button"
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(filter)}
            sx={{
              display: "inline-flex", alignItems: "center", gap: "6px", px: "13px", py: "7px",
              borderRadius: "999px", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
              fontFamily: FONT_FAMILY, transition: "all 0.15s",
              backgroundColor: isActive ? "rgba(253,185,64,0.12)" : "rgba(255,255,255,0.03)",
              border: isActive ? "1px solid rgba(253,185,64,0.45)" : "1px solid rgba(255,255,255,0.08)",
              color: isActive ? AMBER : "rgba(255,255,255,0.75)",
              "&:hover": isActive ? undefined : { borderColor: "rgba(253,185,64,0.4)", color: AMBER },
            }}
          >
            {filter}
            {count !== undefined ? (
              <Box
                component="span"
                sx={{
                  fontSize: "0.7rem", px: "7px", py: "1px", borderRadius: "999px", fontWeight: 700,
                  backgroundColor: isActive ? "rgba(253,185,64,0.18)" : "rgba(255,255,255,0.06)",
                  color: isActive ? AMBER : "rgba(255,255,255,0.6)",
                }}
              >
                {count}
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

// Dark-theme MUI Dialog paper, for FA pages that need a real Dialog (focus trap, Escape,
// scroll lock, ARIA) rather than the hand-rolled fixed-inset overlay QEOversight.js uses —
// that pattern doesn't generalise once a form has its own Select menus, which portal
// above a plain overlay's backdrop. Modelled on the only other dark-Dialog precedent in
// the app (Features/QE/InstallDetailWrapper.js's dialogPaperSx).
export const FA_DIALOG_PAPER_SX = {
  "& .MuiDialog-paper": {
    backgroundColor: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: "16px",
    color: "#fff",
  },
  "& .MuiDialogTitle-root": { color: "#fff" },
  "& .MuiDialogContentText-root": { color: "rgba(255,255,255,0.6)" },
};

// Dark-theme MUI Menu paper — same palette as SELECT_MENU_PROPS above, for a row-actions
// kebab Menu (which portals to document.body and so isn't clipped by a card's own
// overflow:hidden, unlike the design's own in-DOM dropdown).
export const FA_MENU_PAPER_SX = {
  backgroundColor: CARD_BG,
  border: `1px solid ${BORDER}`,
  "& .MuiMenuItem-root": { color: "#fff", fontSize: "0.86rem" },
  "& .MuiMenuItem-root:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
  // The colour has to live here, not on the danger MenuItem's own sx: the rule above is
  // (0,2,0) and an item-level sx is (0,1,0), so a per-item colour silently lost and the
  // danger rows on Team, Fleet and Devices all rendered plain white.
  '& .MuiMenuItem-root[data-danger="true"]': { color: FA_DANGER_MENU_COLOR },
};

/**
 * The design's .notes-textarea, which is `background:#0D1117; border:1px solid
 * rgba(255,255,255,0.1); border-radius:8px; padding:10px 12px; font-size:0.86rem;
 * line-height:1.5` in five of its eight declarations — the values HSNotesBlock already
 * used. Two other skins had grown alongside it: the stock forms used a 10px radius, a
 * lighter border and 0.88rem, and hardcoded #0d1117 rather than the BG token it equals.
 *
 * min-height stays a prop because the design genuinely varies it (60–110px).
 */
export function FANotesField({ value, onChange, placeholder, minHeight = "92px", sx }) {
  return (
    <Box
      component="textarea"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      sx={{
        width: "100%",
        minHeight,
        resize: "vertical",
        borderRadius: "8px",
        border: "1px solid rgba(255,255,255,0.1)",
        backgroundColor: BG,
        color: "#fff",
        padding: "10px 12px",
        fontFamily: FONT_FAMILY,
        fontSize: "0.86rem",
        lineHeight: 1.5,
        outline: "none",
        boxSizing: "border-box",
        "&:focus": { borderColor: "rgba(253,185,64,0.4)" },
        "&::placeholder": { color: "rgba(255,255,255,0.3)" },
        ...sx,
      }}
    />
  );
}

/**
 * The design's .empty-state. Its colour and size are invariant across all seven
 * declarations (0.55 alpha, 0.95rem); only the vertical padding varies, which the design
 * does too (60–80px). Dev had grown a second family at 0.5/0.88rem on the H&S steps and
 * QE oversight, so the same "nothing here" message read differently depending on the page.
 *
 * `card` wraps it in the register-card surface, which the list pages use so the card
 * outline does not vanish when the list is empty.
 */
/**
 * Says a team figure is incomplete. Totals that attribute job cards to the team go through
 * each installer's linked Userport login (team_member_user_ids), so anyone without one is
 * left out — the backend counts them as `unlinked_installers` precisely so a short total
 * does not read as a complete one. Renders nothing when everyone is linked.
 */
export function FAUnlinkedNote({ count, sx }) {
  if (!count) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", backgroundColor: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.22)", borderRadius: "8px", p: "10px 14px", mb: "18px", ...sx }}>
      <InfoOutlinedIcon sx={{ color: WARN_YELLOW, fontSize: "14px", flexShrink: 0 }} />
      <Typography sx={{ color: "rgba(251,191,36,0.95)", fontSize: "0.78rem", lineHeight: 1.4 }}>
        Excludes {count} installer{count === 1 ? "" : "s"} without a linked login — their installs
        are not counted here until IMS links them.
      </Typography>
    </Box>
  );
}

export function FAEmptyState({ children, py = "70px", card }) {
  const text = (
    <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.95rem", textAlign: "center" }}>
      {children}
    </Typography>
  );
  if (!card) return <Box sx={{ py, px: "20px", textAlign: "center" }}>{text}</Box>;
  return (
    <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", px: "20px", py, textAlign: "center" }}>
      {text}
    </Box>
  );
}

/**
 * Renders a status pill from a domain lookup's { label, bg, color }.
 *
 * The tables stay where they are — each is genuinely page-specific — but the markup was
 * byte-identical on Fleet and Devices and near-identical elsewhere, with the radius split
 * three ways across the app.
 */
export function FAStatusPill({ meta, sx }) {
  if (!meta) return null;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex", alignItems: "center", fontSize: "0.66rem", fontWeight: 700,
        px: "9px", py: "3px", borderRadius: "999px", letterSpacing: "0.03em",
        whiteSpace: "nowrap", backgroundColor: meta.bg, color: meta.color, ...sx,
      }}
    >
      {meta.label}
    </Box>
  );
}

/**
 * The design's .stat-tile. Its surface is invariant across all five declarations
 * (#161B22, 0.06 border, 12px radius, 14/16 padding), and .stat-value is 1.45rem amber
 * with tabular numerals in the dominant three.
 *
 * Three components called StatTile existed. Stock's matched the design and is what Fleet
 * and Devices already imported; Home's had drifted to 1.5rem without tabular numerals and
 * moved the info tooltip to an absolute corner, so the same tile rendered two ways.
 *
 * `tone` covers the two legitimate variants:
 *   default — amber value (the design's dominant .stat-value)
 *   "plain" — white value, which qe-oversight.html's own .stat-value specifies
 *   "warn"  — amber-tinted card with a warning value, for an outstanding count
 */
export function FAStatTile({ label, value, sub, subColor, info, tone }) {
  const warn = tone === "warn";
  return (
    <Box
      sx={{
        backgroundColor: warn ? "rgba(251,191,36,0.04)" : CARD_BG,
        border: `1px solid ${warn ? "rgba(251,191,36,0.25)" : BORDER}`,
        borderRadius: "12px", px: "16px", py: "14px",
      }}
    >
      <Typography
        sx={{
          fontSize: "0.62rem", fontWeight: 700, color: "rgba(255,255,255,0.45)",
          textTransform: "uppercase", letterSpacing: "0.08em", mb: "5px",
          display: "inline-flex", alignItems: "center", gap: "5px",
        }}
      >
        {label}
        {info ? (
          <Tooltip title={info} arrow placement="top">
            <InfoOutlinedIcon sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.3)", cursor: "help" }} />
          </Tooltip>
        ) : null}
      </Typography>
      <Typography
        sx={{
          fontSize: "1.45rem", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.05,
          fontVariantNumeric: "tabular-nums",
          color: warn ? WARN_YELLOW : tone === "plain" ? "#fff" : AMBER,
        }}
      >
        {value}
      </Typography>
      {sub ? (
        <Typography sx={{ fontSize: "0.7rem", color: subColor || "rgba(255,255,255,0.5)", mt: "3px" }}>{sub}</Typography>
      ) : null}
    </Box>
  );
}

/**
 * A record/navigation row: icon chip, name + sub, and a CTA or count on the right.
 *
 * Lived in the H&S hub, so Home had to import a component from another page — and
 * from a subfolder it does not otherwise belong to — to render the same row.
 */

export function RecordRow({ Icon, name, sub, isLast, cta, onClick, info }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex", alignItems: "center", gap: "12px", px: "16px", py: "14px",
        borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.04)",
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { backgroundColor: "rgba(253,185,64,0.04)" } : undefined,
      }}
    >
      {/* index.html .record-icon is a 30px rounded chip, not a bare glyph. */}
      <Box sx={{ width: "30px", height: "30px", borderRadius: "7px", backgroundColor: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.7)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon sx={{ fontSize: "1rem" }} />
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography sx={{ color: "#fff", fontSize: "0.88rem", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: "5px" }}>
          {name}
          {info ? (
            <Tooltip title={info} arrow placement="top">
              <InfoOutlinedIcon sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.3)", cursor: "help" }} />
            </Tooltip>
          ) : null}
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.74rem", mt: "1px" }}>{sub}</Typography>
      </Box>
      {cta ? (
        <Typography sx={{ fontSize: "0.76rem", fontWeight: 700, color: AMBER, flexShrink: 0 }}>{cta}</Typography>
      ) : (
        <Box sx={{ fontSize: "0.66rem", fontWeight: 700, color: "rgba(253,185,64,0.85)", backgroundColor: "rgba(253,185,64,0.12)", px: "9px", py: "3px", borderRadius: "999px", flexShrink: 0 }}>
          Coming soon
        </Box>
      )}
    </Box>
  );
}
